import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { listAlbums, type HostAlbum } from "../../lib/hostApi";
import { fmtDate } from "../../lib/format";
import { TRIAL } from "../../lib/wizard";
import { uploadClosed } from "../../lib/timewindow";
import { useT, type Key } from "../../lib/i18n";

export const Route = createFileRoute("/host/")({ component: Dashboard });

const STATUS_KEY: Record<string, Key> = {
  active: "host.list.status.active",
  pending_payment: "host.list.status.pending_payment",
  draft: "host.list.status.draft",
  expired: "host.list.status.expired",
};

function Dashboard() {
  const { t } = useT();
  const albums = useQuery({ queryKey: ["albums"], queryFn: listAlbums });
  const empty = albums.data?.items.length === 0;

  if (albums.isPending) return <p>{t("host.list.loading")}</p>;
  if (albums.error) return <p>{t("host.list.load_failed")}</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl text-ink">{t("host.list.title")}</h1>
        <div className="flex items-center gap-2">
          {albums.data.trial_available && (
            <Link to="/host/albums/new" search={{ trial: true }} className="btn-secondary px-4 py-2 text-sm">
              {t("host.list.try_free")}
            </Link>
          )}
          <Link to="/host/albums/new" className="btn-primary px-4 py-2 text-sm">
            {t("host.wizard.create")}
          </Link>
        </div>
      </div>
      {empty && (
        <p className="text-sm text-neutral-500">{t("host.list.empty", { guests: TRIAL.guests, days: TRIAL.days })}</p>
      )}
      <ul className="space-y-2">
        {albums.data.items.map((a: HostAlbum) => (
          <li key={a.id}>
            <Link to="/host/albums/$id" params={{ id: String(a.id) }} className="card block">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium">{a.title ?? t("host.untitled")}</span>
                <span className="shrink-0 text-xs text-neutral-500">
                  {a.status === "active" && uploadClosed(a, Date.now() / 1000)
                    ? t("host.list.status.done")
                    : STATUS_KEY[a.status]
                      ? t(STATUS_KEY[a.status])
                      : a.status}
                </span>
              </div>
              <p className="text-sm text-neutral-500">
                {fmtDate(a.event_start)} · {t("host.list.photos_count", { count: a.photo_count, total: a.photo_quota })}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
